<template>
	<div class="content-header has-bottom-divider has-padding-top-3 has-margin">
		<div class="field is-grouped is-flex-grow-1 is-align-items-end">
			<VLink
				v-if="canEditMethod"
				:text="$t('method.methodList.addMethod')"
				icon="plus"
				:hasUnderline="true"
				@click.prevent.stop="createNewClicked"
				class="has-margin-right-3 has-margin-bottom-05"
			/>

			<VSearch v-model="search" />
		</div>
	</div>

	<div class="scrollable-content">
		<SortableDataTable
			:scrollable="false"
			:rowClickedFn="rowClicked"
			defaultSort="last_time_edited"
			sortDirection="DESC"
			:data="filteredData"
			:columns="columns"
			:emptyText="$t('method.methodList.table.noResults')"
		/>
	</div>
</template>

<script>
import { useStore } from 'vuex';
import { reactive, toRefs } from '@vue/reactivity';
import { getStoreAction, getStoreMutation } from '@assets/scripts/store/config';
import { formatForTable } from '@modules/MethodBuilder/components/method';
import Helpers from '@assets/scripts/helpers';
import { useApiAsync } from '@assets/scripts/composables/useApi';
import { useCreateNewOnKeydown } from '@assets/scripts/composables/useCreateNewOnKeydown';
import usePermission from '@assets/scripts/composables/usePermission';
import { GET_METHODS } from '@modules/MethodBuilder/endpoints';

export default {
	name: 'MethodList',
	props: {
		connGuid: {
			type: String,
			required: true,
		}
	},
	data: function () {
		return {
			search: '',
		};
	},
	async setup (props) {
		const store = useStore();

		const state = reactive({
			methods: [],
			/**
			 * Boolean to indicate whether the current
			 * user can create methods
			 */
			canEditMethod: usePermission('Upsert', 'MethodBuilder'),
		});

		const createNewClicked = () => {
			store.dispatch(
				getStoreAction('TOGGLE_CREATE_NEW', 'METHOD'),
				true
			);
		};
		if(state.canEditMethod) {
			useCreateNewOnKeydown(createNewClicked);
		}

		const loadMethods = async () => {
			// get all Connection Methods
			state.methods = await useApiAsync(GET_METHODS, {
				keys: {
					connection: props.connGuid
				}
			});
		};

		// subscribe to store action
		const unsubscribeAction = store.subscribeAction({
			after: async ({ type, payload }) => {
				// check action type and relevant connection GUID
				if (
					type === getStoreAction('UPDATE_METHOD_LIST', 'METHOD') &&
					payload === props.connGuid
				) {
					// reload method list if needed
					await loadMethods();
				}
			}
		});

		await loadMethods();

		return { ...toRefs(state), unsubscribeAction, createNewClicked };
	},
	computed: {
		columns: function() {
			return [
				{
					label: this.$t('method.methodList.table.columns.name'),
					field: 'name',
					sortable: true,
					searchable: true,
					cellClass: 'has-text-weight-semibold',
				},
				{
					label: this.$t('method.methodList.table.columns.last_time_edited'),
					field: 'last_time_edited',
					sortable: true,
					searchable: true,
					isDate: true,
				},
				{
					label: this.$t('method.methodList.table.columns.status'),
					field: 'status',
					sortable: true,
					searchable: true,
				},
				{
					label: '',
					field: 'edit',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						edit: {
							title: this.$t('general.edit'),
							isTool: true,
							icon: 'edit',
						},
						view: {
							title: this.$t('general.inspect'),
							isTool: true,
							icon: 'eye',
						}
					},
					click: this.rowClicked,
				},
				{
					label: '',
					field: 'delete',
					sortable: false,
					default: '',
					component: 'VButton',
					cellClass: 'is-button-tool',
					args: {
						href: '',
						title: this.$t('general.delete'),
						isTool: true,
						class: '',
						icon: 'delete',
					},
					click: this.deleteClicked,
				},
			];
		},
		tableFields: function () {
			return formatForTable(this.methods);
		},
		searchableCols: function () {
			return Helpers.getSearchableColumns(this.columns);
		},
		filteredData: function () {
			// get all methods
			const newData = this.tableFields;

			// return if no rows match
			if (this.search.length === 0 || newData.length === 0) return newData;

			// filter on search string
			return Helpers.filterByString(
				newData,
				this.search,
				this.searchableCols
			);
		},
	},
	methods: {
		rowClicked({ guid }) {
			// dispatch action to show clicked method
			this.$store.dispatch(getStoreAction('LOAD_AND_SHOW_METHOD', 'METHOD'), {
				guid,
				conn_guid: this.connGuid,
			});
		},
		deleteClicked({ guid }) {
			// ask confirmation to delete method
			this.$store.commit(getStoreMutation('OPEN_CONFIRMATION'), {
				title: this.$t('method.methodList.delete.confirm.title'),
				body: this.$t('method.methodList.delete.confirm.body'),
				confirmButtonText: this.$t(
					'method.methodList.delete.confirm.confirmButtonText'
				),
				confirmFn: () => {
					// dispatch action to delete clicked method
					this.$store.dispatch(
						getStoreAction('DELETE_METHOD', 'METHOD'),
						{
							guid,
							conn_guid: this.connGuid,
						}
					);
				},
			});
		},
		sortByDate(a, b, isAsc) {
			return Helpers.date.sortByIsoDate(a.time, b.time, isAsc);
		},
	},
	unmounted: function( ) {
		// unsubscribe from actions
		this.unsubscribeAction();
	},
};
</script>
